import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowLeft02Icon, ArrowRight02Icon, Cancel01Icon } from '@hugeicons-pro/core-stroke-rounded'
import {
    Box,
    Button,
    ButtonGroup,
    Dialog,
    DialogContent,
    FormControl,
    IconButton,
    InputLabel,
    ListItemText,
    Menu,
    MenuItem,
    Select,
    ThemeProvider,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import {
    selectOrderItemsForOrder,
    useAddOrderItemsFromPartsMutation,
    useDeleteOrderItemMutation,
    useGetOrderItemDrawingQuery,
    useGetOrderItemsQuery,
    useResetOrderItemDrawingMutation,
    useUpdateOrderItemDrawingMutation,
} from '@/app/services/orderItems'
import { useCalculateOrderMutation } from '@/app/services/orders'
import { selectShowDrawingDoctorLegend, toggleShowDrawingDoctorLegend } from '@/app/slices/appSlice'
import {
    addItemToUpdateQueue,
    removeItemFromUpdateQueue,
    selectIsUpdateQueueEmpty,
    selectOrderItemDrawingId,
    setSelectedOrderItemDrawingId,
} from '@/app/slices/orderItemsSlice'
import {
    selectOrganisation,
    selectOrganisationId,
    selectWebStoreDrawingDoctorEnabled,
    selectWebStoreFoldingEnabled,
} from '@/app/slices/organisationSlice'
import { AlertDialog, LoadingOverlay, TbxTooltip } from '@/common/components'
import { API_BASE_URL } from '@/common/config/api'
import useSimpleKeyboardShortcut from '@/common/hooks/useSimpleKeyboardShortcut'
import { theme as darkTheme } from '@/common/themes/DarkTheme'
import { StoreThemeOverride } from '@/common/themes/overrides/StoreThemeOverride'
import { orderHasUnsetItems } from '@/common/utils/OrderUtils/OrderUtilities'

import DrawingDoctorDrawer from '../DrawingDoctor/Components/DrawingDoctorDrawer/DrawingDoctorDrawer'
import ExtractPartDialog from '../DrawingDoctor/Components/ExtractPartDialog'
import DrawingDoctor from '../DrawingDoctor/DrawingDoctor'
import Drawing from '../DrawingDoctor/Entities/Drawing'
import ScaleDrawingModal from '../ScaleDrawingModal/ScaleDrawingModal'

import RenderDimensions from './RenderDimensions'

const classes = {
    dialogRoot: {
        overflow: 'hidden',
    },
    topBar: {
        borderBottom: '1px solid hsl(0, 0%, 35%)',
        boxSizing: 'border-box',
        height: 72,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: 'hsl(0, 0%, 15%)',
        gap: 2,
    },
    topBarTitle: {
        width: 400,
        py: 3,
        px: 1,
    },
    topBarActions: {
        justifyContent: 'space-around',
        flex: 1,
    },
    // A rotary part's size plus its thickness can outgrow the fixed width, so the box grows to fit
    // it on one line and the actions area beside it (flex: 1) gives up the space.
    topBarInfo: (isTubePart) => ({
        ...(isTubePart ? { flexShrink: 0, minWidth: 400, width: 'auto' } : { width: 260 }),
        p: 3,
        justifyContent: 'flex-end',
    }),
    flex: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    dialogContent: (showCadView) => ({
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: showCadView ? 'hsl(0, 0%, 0%)' : 'hsl(0, 0%, 25%)',
        boxSizing: 'border-box',
        overflow: 'hidden',
        p: 0,
    }),
    toggleButtonGroup: {
        backgroundColor: 'primary.main',
    },
    toggleButton: {
        color: 'primary.contrastText',
        padding: '8px 12px',
        '&.Mui-selected': {
            color: 'primary.contrastText',
        },
    },
    toggleButtonGroupSecondary: {
        backgroundColor: 'secondary.main',
    },

    toggleButtonSecondary: {
        color: 'secondary.contrastText',
        padding: '6px 12px',
        borderColor: 'secondary.main',
        '&.Mui-selected': {
            color: 'secondary.contrastText',
            backgroundColor: 'secondary.dark',
        },
    },
}

const DrawingDoctorModal = ({ onClose, open }) => {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const params = useParams()
    const { orderId } = params
    const dispatch = useDispatch()

    const organisationId = useSelector(selectOrganisationId)
    const storeSettings = useSelector(selectOrganisation)
    const orderItemId = useSelector(selectOrderItemDrawingId)
    const orderItems = useSelector((state) => selectOrderItemsForOrder(state, { organisationId, orderId }))
    const showLegend = useSelector(selectShowDrawingDoctorLegend)
    const foldingEnabled = useSelector(selectWebStoreFoldingEnabled)
    const drawingDoctorEnabled = useSelector(selectWebStoreDrawingDoctorEnabled)
    const isUpdateQueueEmpty = useSelector(selectIsUpdateQueueEmpty)

    const { orderItem } = useGetOrderItemsQuery(
        {
            organisationId,
            orderId,
        },
        {
            selectFromResult: ({ data }) => ({
                orderItem: data?.find((item) => item.id === orderItemId),
            }),
        }
    )

    const [uploadOrderItems] = useAddOrderItemsFromPartsMutation()
    const [deleteOrderItem] = useDeleteOrderItemMutation()
    const [updateOrderItemDrawing] = useUpdateOrderItemDrawingMutation()
    const [calculateOrderMutation, { isLoading: isCalculating }] = useCalculateOrderMutation({
        fixedCacheKey: 'shared-calculate-order',
    })
    const [resetDrawing] = useResetOrderItemDrawingMutation()

    const { data: rawDrawing, isLoading: isLoadingDrawing } = useGetOrderItemDrawingQuery({
        organisationId,
        orderId,
        orderItemId: orderItemId,
    })

    const [cadView, setCadView] = useState(null)
    const [showShortcuts, setShowShortcuts] = useState(false)
    const [showCadView, setShowCadView] = useState(false)
    const [showExtractPartDialog, setShowExtractPartDialog] = useState(false)
    const [showExtractAllPartsDialog, setShowExtractAllPartsDialog] = useState(false)
    const [anchorEl, setAnchorEl] = useState(null)
    const [actionMenu, setActionMenu] = useState(null)
    const [selectedOrderItemId, setSelectedOrderItemId] = useState(orderItemId)
    const [drawing, setDrawingState] = useState({})
    const [selectedPaths, setSelectedPaths] = useState({})
    const [isUpdatingDrawing, setIsUpdatingDrawing] = useState(false)
    const [needsRecalculation, setNeedsRecalculation] = useState(false)
    const [resetDrawingDialog, setResetDrawingDialog] = useState(false)
    const [showScaleDrawingModal, setShowScaleDrawingModal] = useState(false)

    const numberOfSelectedPaths = Object.keys(selectedPaths).length
    const viewOnlyMode = !drawingDoctorEnabled

    const isPartFlat = orderItem?.profile === undefined || orderItem?.profile === null || orderItem?.profile === ''
    const isTubePart = Boolean(orderItem) && !isPartFlat
    const actionMenuOpen = Boolean(anchorEl)

    const canCalculateOrder = useMemo(() => {
        return !orderHasUnsetItems(orderItems)
    }, [orderItems])

    const platform = window.navigator.platform.toLowerCase()
    const isMacOS = platform.indexOf('mac') !== -1

    const orderItemIndex = useMemo(() => {
        return orderItems.findIndex((item) => item.id === orderItem?.id)
    }, [orderItems, orderItem])

    const isPartFromLibrary = useMemo(() => {
        return Boolean(orderItem?.partLibraryEntryId)
    }, [orderItem])

    const isSelectedPathsEmpty = useMemo(() => {
        return Object.keys(selectedPaths).length === 0
    }, [selectedPaths])

    const resolvedDarkTheme = useMemo(
        () => (StoreThemeOverride ? StoreThemeOverride(darkTheme, storeSettings) : darkTheme),
        [storeSettings]
    )

    const showPrevButton = orderItemIndex > 0
    const showNextButton = orderItemIndex < orderItems.length - 1

    const drawingDimensions = useMemo(() => {
        if (!isSelectedPathsEmpty && numberOfSelectedPaths === 1) {
            const path = drawing.paths[Object.keys(selectedPaths)[0]]
            return {
                width: path.width,
                height: path.height,
            }
        }

        return {
            width: drawing?.width,
            height: drawing?.height,
        }
    }, [drawing, isSelectedPathsEmpty, numberOfSelectedPaths, selectedPaths])

    const calculateOrder = async () => {
        try {
            await calculateOrderMutation({ organisationId, orderId }).unwrap()
        } catch (error) {
            const errorMessage = t(error.data)
            enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 5000 })
        }
    }

    useEffect(() => {
        if (needsRecalculation && isUpdateQueueEmpty && !isCalculating && !isUpdatingDrawing) {
            setNeedsRecalculation(false)
            calculateOrder()
        }
    }, [needsRecalculation, isUpdateQueueEmpty, isCalculating, isUpdatingDrawing])

    const handleUpdateOrderItemDrawing = async (orderItemDrawing) => {
        setIsUpdatingDrawing(true)
        const updatingOrderItemId = orderItem?.id
        dispatch(addItemToUpdateQueue(updatingOrderItemId))
        try {
            orderItemDrawing.paths = Object.values(orderItemDrawing.paths)
            const drawing = { ...orderItemDrawing }
            const result = await updateOrderItemDrawing({
                organisationId,
                orderId,
                orderItemId: updatingOrderItemId,
                drawing,
            }).unwrap()

            if (canCalculateOrder) setNeedsRecalculation(true)
            enqueueSnackbar(t('Drawing updated'), { variant: 'success' })
            return result
        } catch (error) {
            enqueueSnackbar(t('Failed to update the drawing'), {
                variant: 'error',
            })
        } finally {
            dispatch(removeItemFromUpdateQueue(updatingOrderItemId))
            setIsUpdatingDrawing(false)
        }
    }

    const handleViewChange = (event, newView) => setShowCadView(newView)

    const handleActionClick = (menu) => (event) => {
        setAnchorEl(event.currentTarget)
        setActionMenu(menu)
    }
    const handleActionMenuClose = () => {
        setAnchorEl(null)
        setActionMenu(null)
    }

    const toggleShowLegend = () => {
        dispatch(toggleShowDrawingDoctorLegend())
        handleActionMenuClose()
    }
    const toggleShowShortcuts = () => {
        setShowShortcuts((showShortcuts) => !showShortcuts)
        handleActionMenuClose()
    }

    // Removing error paths
    const handleRemoveNonSolidLinesClick = () => {
        const updatedDrawing = new Drawing(drawing)

        for (const k in updatedDrawing.paths) {
            if (!updatedDrawing.paths[k].isSolid) {
                updatedDrawing.deletePath(k)
            }
        }

        //setDrawingState(updatedDrawing)
        handleUpdateOrderItemDrawing(updatedDrawing)
    }

    const handleRemoveOpenInnersClick = () => {
        const updatedDrawing = new Drawing(drawing)

        for (const k in updatedDrawing.paths) {
            if (!updatedDrawing.paths[k].isClosed && updatedDrawing.paths[k].layer?.type !== 'Folding') {
                updatedDrawing.deletePath(k)
            }
        }

        //setDrawingState(updatedDrawing)
        handleUpdateOrderItemDrawing(updatedDrawing)
    }

    // Extracting paths
    const handleExtractSelectedClick = () => {
        setShowExtractPartDialog(true)
    }

    const handleExtractAllPartsClick = () => {
        setShowExtractAllPartsDialog(true)
    }

    const hideShowExtractPartDialog = () => {
        setShowExtractPartDialog(false)
    }

    const hideShowExtractAllPartDialog = () => {
        setShowExtractAllPartsDialog(false)
    }

    const handleSelectedPathExtractionSubmit = (name) => {
        handleExtractSelectedPaths(name)
        setShowExtractPartDialog(false)
    }

    const handleAllPathExtractionSubmit = (name) => {
        handleExtractAllPaths(name)
        setShowExtractAllPartsDialog(false)
    }

    const extractPathToDrawingDto = (path, name) => {
        const internalPaths = []
        for (let i = 0; i < path.enclosesIds.length; i++) {
            const p = drawing.paths[path.enclosesIds[i]]
            internalPaths.push(p)
        }

        const newDrawing = Drawing.drawingFromOuterPath(name, path, internalPaths)

        return newDrawing.toDto()
    }

    const hasRemainingPaths = (extractedPathIds, outerPathIds) => {
        if (!outerPathIds || outerPathIds.length === 0) return true
        // extracted paths must be at least equal to outer paths if all paths are to be removed
        if (extractedPathIds.length >= outerPathIds.length) {
            for (const pathId of outerPathIds) {
                //if we can't find an outer path in our list of extracted parts there will be paths remaining in drawing
                if (!extractedPathIds.find((id) => id === pathId)) {
                    return true
                }
            }
            // if all outer paths exist in the extracted paths then we know no parts will remain
            return false
        }
        return true
    }

    const handleDeleteSelectedAndInsideClick = () => {
        const updatedDrawing = new Drawing(drawing)

        for (const k in selectedPaths) {
            const pathId = updatedDrawing.paths[k].pathId

            updatedDrawing.deleteInsidePath(pathId)
            updatedDrawing.deletePath(pathId)
        }

        //setDrawingState(updatedDrawing)
        handleUpdateOrderItemDrawing(updatedDrawing)
    }

    const addDrawingsToOrder = (drawings, newPartProps) => {
        const partsToAdd = []
        drawings.forEach((drawing) => {
            const newPart = {
                drawing: drawing,
                partSource: 'drawingFile',
                originalDrawingId: newPartProps.originalDrawingId,
                quantity: newPartProps.quantity ?? 1,
                isImperial: newPartProps.isImperial,
                cuttingTechnologyId: newPartProps.cuttingTechnologyId,
                materialId: newPartProps.materialId,
                sheetId: newPartProps.sheetId,
                thickness: newPartProps.thickness,
            }

            partsToAdd.push(newPart)
        })
        uploadOrderItems({ organisationId, orderId: orderId, parts: partsToAdd })
        onClose()
    }

    const handlePartsExtracted = (partDrawings, originalPart, deleteOriginalPart) => {
        if (deleteOriginalPart) {
            deleteOrderItem({ organisationId, orderId, orderItemId: originalPart.id })
        }

        addDrawingsToOrder(partDrawings, originalPart)
    }

    const handleExtractSelectedPaths = (name) => {
        if (numberOfSelectedPaths > 0) {
            let count = 1

            const pathKeys = Object.keys(selectedPaths)
            const pathsToExtract = []
            const extractedOuterPathKeys = []

            pathKeys.forEach((key) => {
                const path = drawing.paths[key]

                // Skip paths enclosed by another selected path — they'll be
                // included automatically as internal geometry via enclosesIds
                const isEnclosedBySelectedPath = path.enclosedByIds.some((id) => pathKeys.includes(id))
                if (isEnclosedBySelectedPath) return

                extractedOuterPathKeys.push(key)

                const partName = name + ` ${count}`
                count += 1

                const drawingDto = extractPathToDrawingDto(path, partName)
                pathsToExtract.push(drawingDto)
            })
            if (pathsToExtract.length === 0) return

            const deleteOriginalPart = !hasRemainingPaths(extractedOuterPathKeys, drawing?.partOuterPathIds)
            handlePartsExtracted(pathsToExtract, orderItem, deleteOriginalPart)

            if (!deleteOriginalPart) {
                // Remove only the extracted outer paths and their enclosed paths
                // from the original drawing
                const updatedDrawing = new Drawing(drawing)

                extractedOuterPathKeys.forEach((key) => {
                    if (updatedDrawing.paths[key]) {
                        const pathId = updatedDrawing.paths[key].pathId
                        updatedDrawing.deleteInsidePath(pathId)
                        updatedDrawing.deletePath(pathId)
                    }
                })

                handleUpdateOrderItemDrawing(updatedDrawing)
            }
        }
    }

    const handleAllPartsExtracted = (partDrawings, originalPart) => {
        deleteOrderItem({ organisationId, orderId: orderId, orderItemId: orderItem?.id })
        addDrawingsToOrder(partDrawings, originalPart)
    }

    const handleExtractAllPaths = (namePrefix) => {
        let count = 1
        const extractedDrawings = []

        for (const k in drawing.paths) {
            const path = drawing.paths[k]
            if (!path.isText() && !path.layer.isInfo() && path.isOuter()) {
                const partName = namePrefix + ` ${count}`
                count += 1

                const drawingDto = extractPathToDrawingDto(path, partName)
                extractedDrawings.push(drawingDto)
            }
        }

        handleAllPartsExtracted(extractedDrawings, orderItem)
    }

    // Deleting paths
    const handleDeleteSelectedClick = () => {
        const updatedDrawing = new Drawing(drawing)

        for (const k in selectedPaths) {
            updatedDrawing.deletePath(k)
        }

        //setDrawingState(updatedDrawing)
        handleUpdateOrderItemDrawing(updatedDrawing)
        setSelectedPaths({})
    }

    const handleDeleteInsideSelectedClick = () => {
        if (numberOfSelectedPaths === 1) {
            const updatedDrawing = new Drawing(drawing)
            const k = Object.keys(selectedPaths)[0]
            const pathId = updatedDrawing.paths[k].pathId

            updatedDrawing.deleteInsidePath(pathId)

            //setDrawingState(updatedDrawing)
            handleUpdateOrderItemDrawing(updatedDrawing)
            setSelectedPaths({})
        }
    }

    const handleDeleteOutsideSelectedClick = () => {
        if (numberOfSelectedPaths === 1) {
            const updatedDrawing = new Drawing(drawing)
            const k = Object.keys(selectedPaths)[0]
            const pathId = updatedDrawing.paths[k].pathId

            updatedDrawing.deleteOutsidePath(pathId)

            //setDrawingState(updatedDrawing)
            handleUpdateOrderItemDrawing(updatedDrawing)
            setSelectedPaths({})
        }
    }

    // Change path layers
    const changeSelectedEntitiesToCutting = () => {
        changeSelectedEntitiesToLayer('Cutting')
    }

    const changeSelectedEntitiesToEtching = () => {
        changeSelectedEntitiesToLayer('Etching')
    }

    const changeSelectedEntitiesToFolding = () => {
        changeSelectedEntitiesToLayer('Folding')
    }

    const changeSelectedEntitiesToInformation = () => {
        changeSelectedEntitiesToLayer('Information')
    }

    const changeSelectedEntitiesToLayer = (layerName) => {
        enqueueSnackbar(t(`Changing selected entities to ${layerName} layer`), {
            variant: 'info',
        })

        const selectedPathKeys = Object.keys(selectedPaths)

        const updatedDrawing = new Drawing(drawing)

        selectedPathKeys.forEach((key) => {
            updatedDrawing.paths[key].layer.type = layerName
            updatedDrawing.paths[key].entities.forEach((entity) => {
                entity.layer.type = layerName
            })
        })

        //setDrawingState(updatedDrawing)
        handleUpdateOrderItemDrawing(updatedDrawing)
        setSelectedPaths({})
    }

    const handleSelectOrderItemChange = (event) => {
        setSelectedPaths({})
        const selectedOrderItemId = event.target.value
        setSelectedOrderItemId(selectedOrderItemId)
        dispatch(setSelectedOrderItemDrawingId(selectedOrderItemId))
    }

    const handlePrevPartClick = () => {
        setSelectedPaths({})
        const prevOrderItem = orderItems[orderItemIndex - 1]

        if (prevOrderItem) {
            setSelectedOrderItemId(prevOrderItem.id)
            dispatch(setSelectedOrderItemDrawingId(prevOrderItem.id))
        }
    }

    const handleNextPartClick = () => {
        setSelectedPaths({})
        const nextOrderItem = orderItems[orderItemIndex + 1]

        if (nextOrderItem) {
            setSelectedOrderItemId(nextOrderItem.id)
            dispatch(setSelectedOrderItemDrawingId(nextOrderItem.id))
        }
    }

    const handleResetDrawingButtonClick = () => {
        setResetDrawingDialog(true)
    }

    const handleResetDrawing = async () => {
        try {
            await resetDrawing({
                organisationId,
                orderId,
                orderItemId,
            }).unwrap()

            enqueueSnackbar(t('Drawing reset'), { variant: 'success' })
        } catch (error) {
            enqueueSnackbar(t('Failed to reset the drawing'), {
                variant: 'error',
            })
        }
    }

    useEffect(() => {
        if (!rawDrawing) {
            return
        }

        const drawingObj = new Drawing(structuredClone(rawDrawing))

        if (!drawingObj) {
            return
        }

        setDrawingState(drawingObj)
    }, [rawDrawing])

    useEffect(() => {
        if (!orderItem?.id) return
        if (isTubePart) setShowCadView(true)
    }, [orderItem?.id, isTubePart])

    useEffect(() => {
        if (!orderItem?.id) return

        fetch(`${API_BASE_URL}/store/${organisationId}/${orderId}/quote-items/${orderItem?.id}/cad-view`)
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Network response was not ok')
                }
                return response.text() // SVG is typically returned as text
            })
            .then((svgData) => setCadView(svgData))
            .catch((error) => {
                console.error('There was a problem with the fetch operation:', error)
            })
    }, [orderItem, organisationId, orderId])

    // Keyboard shortcuts using the new simple hook
    // Delete shortcuts - only when entities are selected and editable
    useSimpleKeyboardShortcut('Delete', handleDeleteSelectedClick, {
        disabled: viewOnlyMode || isSelectedPathsEmpty || isUpdatingDrawing || isPartFromLibrary,
    })

    useSimpleKeyboardShortcut('Backspace', handleDeleteSelectedClick, {
        disabled: viewOnlyMode || isSelectedPathsEmpty || isUpdatingDrawing || isPartFromLibrary,
    })

    // Entity type shortcuts - Alt + key combinations for selected entities
    useSimpleKeyboardShortcut(['Alt', 'c'], changeSelectedEntitiesToCutting, {
        disabled: viewOnlyMode || isSelectedPathsEmpty || isUpdatingDrawing || isPartFromLibrary,
    })

    useSimpleKeyboardShortcut(['Alt', 'e'], changeSelectedEntitiesToEtching, {
        disabled: viewOnlyMode || isSelectedPathsEmpty || isUpdatingDrawing || isPartFromLibrary,
    })

    useSimpleKeyboardShortcut(['Alt', 'i'], changeSelectedEntitiesToInformation, {
        disabled: viewOnlyMode || isSelectedPathsEmpty || isUpdatingDrawing || isPartFromLibrary,
    })

    useSimpleKeyboardShortcut(['Alt', 'f'], changeSelectedEntitiesToFolding, {
        disabled: viewOnlyMode || isSelectedPathsEmpty || isUpdatingDrawing || isPartFromLibrary || !foldingEnabled,
    })

    // UI toggle shortcuts - Alt + key combinations available in all modes
    useSimpleKeyboardShortcut(['Alt', 's'], toggleShowShortcuts, {
        disabled: viewOnlyMode,
    })

    useSimpleKeyboardShortcut(['Alt', 'p'], toggleShowLegend, {
        disabled: viewOnlyMode,
    })

    return (
        <ThemeProvider theme={resolvedDarkTheme}>
            <Dialog
                open={open}
                sx={classes.dialogRoot}
                disableEscapeKeyDown
                fullScreen
                onClose={onClose}
            >
                <DialogContent sx={classes.dialogContent(showCadView)}>
                    <Box sx={classes.topBar}>
                        <Box sx={[classes.topBarTitle, classes.flex]}>
                            <IconButton
                                color="primary"
                                disabled={!showPrevButton}
                                onClick={handlePrevPartClick}
                            >
                                <HugeiconsIcon
                                    icon={ArrowLeft02Icon}
                                    size={18}
                                />
                            </IconButton>
                            <FormControl fullWidth>
                                <InputLabel
                                    color="primary"
                                    id="select-order-item-option-label"
                                    shrink={true}
                                >
                                    {t('Select part')}
                                </InputLabel>
                                <Select
                                    color="primary"
                                    id="order-item-option"
                                    inputProps={{
                                        'data-testid': 'order-item-option-input',
                                    }}
                                    label={t('Shipping option')}
                                    labelId="order-item-option-label"
                                    value={selectedOrderItemId}
                                    variant="standard"
                                    onChange={handleSelectOrderItemChange}
                                >
                                    {orderItems.map((option) => (
                                        <MenuItem
                                            key={option.id}
                                            value={option.id}
                                        >
                                            {t(option.name)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <IconButton
                                color="primary"
                                disabled={!showNextButton}
                                onClick={handleNextPartClick}
                            >
                                <HugeiconsIcon
                                    icon={ArrowRight02Icon}
                                    size={18}
                                />
                            </IconButton>
                        </Box>

                        <Box sx={[classes.topBarActions, classes.flex]}>
                            {!isTubePart ? (
                                <ToggleButtonGroup
                                    data-testid="order-item-toolbar-button-toggle-cad-view"
                                    size="small"
                                    sx={classes.toggleButtonGroupSecondary}
                                    value={showCadView}
                                    exclusive
                                    onChange={handleViewChange}
                                >
                                    <ToggleButton
                                        sx={classes.toggleButtonSecondary}
                                        value={false}
                                    >
                                        {viewOnlyMode ? t('Cutting plan') : t('Edit mode')}
                                    </ToggleButton>
                                    <ToggleButton
                                        sx={classes.toggleButtonSecondary}
                                        value={true}
                                    >
                                        {isPartFlat ? t('Original Drawing') : t('3D Model')}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            ) : null}

                            {!viewOnlyMode && !isTubePart ? (
                                <ButtonGroup
                                    variant="contained"
                                    disableElevation
                                >
                                    {isPartFlat ? (
                                        <TbxTooltip
                                            title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                                            arrow
                                        >
                                            <span>
                                                <Button
                                                    disabled={showCadView || isPartFromLibrary}
                                                    onClick={() => setShowScaleDrawingModal(true)}
                                                >
                                                    {t('Scale')}
                                                </Button>
                                            </span>
                                        </TbxTooltip>
                                    ) : null}

                                    <TbxTooltip
                                        title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                                        arrow
                                    >
                                        <span>
                                            <Button
                                                disabled={showCadView || isPartFromLibrary}
                                                onClick={handleActionClick('delete')}
                                            >
                                                {t('Delete')}
                                            </Button>
                                        </span>
                                    </TbxTooltip>

                                    <TbxTooltip
                                        title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                                        arrow
                                    >
                                        <span>
                                            <Button
                                                disabled={showCadView || isPartFromLibrary}
                                                onClick={handleActionClick('line-entity')}
                                            >
                                                {t('Process type')}
                                            </Button>
                                        </span>
                                    </TbxTooltip>

                                    <TbxTooltip
                                        title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                                        arrow
                                    >
                                        <span>
                                            <Button
                                                disabled={showCadView || isPartFromLibrary}
                                                onClick={handleActionClick('extract')}
                                            >
                                                {t('Extract')}
                                            </Button>
                                        </span>
                                    </TbxTooltip>

                                    <TbxTooltip
                                        title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                                        arrow
                                    >
                                        <span>
                                            <Button
                                                disabled={showCadView || isPartFromLibrary}
                                                onClick={handleResetDrawingButtonClick}
                                            >
                                                {t('Reset drawing')}
                                            </Button>
                                        </span>
                                    </TbxTooltip>

                                    <Button onClick={handleActionClick('help')}>{t('Help')}</Button>
                                </ButtonGroup>
                            ) : null}

                            <Menu
                                anchorEl={anchorEl}
                                id="delete-action-menu"
                                open={actionMenuOpen ? actionMenu === 'delete' : null}
                                onClose={handleActionMenuClose}
                            >
                                <MenuItem
                                    disabled={isSelectedPathsEmpty || isUpdatingDrawing}
                                    onClick={handleDeleteSelectedClick}
                                >
                                    <ListItemText>{t('Delete selected paths')}</ListItemText>
                                    <Typography variant="body2">{'\u232B'}</Typography>
                                </MenuItem>
                                <MenuItem
                                    disabled={isSelectedPathsEmpty || isUpdatingDrawing}
                                    onClick={handleDeleteInsideSelectedClick}
                                >
                                    {t('Delete paths inside the selection')}
                                </MenuItem>
                                <MenuItem
                                    disabled={isSelectedPathsEmpty || isUpdatingDrawing}
                                    onClick={handleDeleteOutsideSelectedClick}
                                >
                                    {t('Delete paths outside the selection')}
                                </MenuItem>
                                <MenuItem
                                    disabled={isSelectedPathsEmpty || isUpdatingDrawing}
                                    onClick={handleDeleteSelectedAndInsideClick}
                                >
                                    {t('Delete selected paths and paths inside the selection')}
                                </MenuItem>

                                <MenuItem
                                    disabled={isUpdatingDrawing}
                                    onClick={handleRemoveNonSolidLinesClick}
                                >
                                    {t('Delete all dashed paths')}
                                </MenuItem>
                                <MenuItem
                                    disabled={isUpdatingDrawing}
                                    onClick={handleRemoveOpenInnersClick}
                                >
                                    {t('Delete all open paths inside parts')}
                                </MenuItem>
                            </Menu>

                            <Menu
                                anchorEl={anchorEl}
                                id="line-entity-action-menu"
                                open={actionMenuOpen ? actionMenu === 'line-entity' : null}
                                onClose={handleActionMenuClose}
                            >
                                <MenuItem
                                    disabled={isSelectedPathsEmpty || isUpdatingDrawing}
                                    onClick={changeSelectedEntitiesToCutting}
                                >
                                    <ListItemText>{t('Set path’s process to cutting')}</ListItemText>
                                    <Typography variant="body2">{`${isMacOS ? '\u2325' : 'Alt +'} C`}</Typography>
                                </MenuItem>
                                <MenuItem
                                    disabled={isSelectedPathsEmpty || isUpdatingDrawing}
                                    onClick={changeSelectedEntitiesToEtching}
                                >
                                    <ListItemText> {t('Set path’s process to etching')}</ListItemText>
                                    <Typography variant="body2">{`${isMacOS ? '\u2325' : 'Alt +'} E`}</Typography>
                                </MenuItem>
                                {foldingEnabled ? (
                                    <MenuItem
                                        disabled={isSelectedPathsEmpty || isUpdatingDrawing}
                                        onClick={changeSelectedEntitiesToFolding}
                                    >
                                        <ListItemText>{t("Set path's process to folding")}</ListItemText>
                                        <Typography variant="body2">{`${isMacOS ? '\u2325' : 'Alt +'} F`}</Typography>
                                    </MenuItem>
                                ) : null}
                                <MenuItem
                                    disabled={isSelectedPathsEmpty || isUpdatingDrawing}
                                    onClick={changeSelectedEntitiesToInformation}
                                >
                                    <ListItemText sx={{ marginRight: 4 }}>
                                        {t('Set path’s process to information only')}
                                    </ListItemText>
                                    <Typography variant="body2">{`${isMacOS ? '\u2325' : 'Alt +'} I`}</Typography>
                                </MenuItem>
                            </Menu>

                            <Menu
                                anchorEl={anchorEl}
                                id="extract-action-menu"
                                open={actionMenuOpen ? actionMenu === 'extract' : null}
                                onClose={handleActionMenuClose}
                            >
                                <MenuItem
                                    disabled={isSelectedPathsEmpty || isUpdatingDrawing}
                                    onClick={handleExtractSelectedClick}
                                >
                                    {t('Extract selected parts to their own quote items')}
                                </MenuItem>
                                <MenuItem
                                    className="drawing-doctor-menu-extract-all"
                                    onClick={handleExtractAllPartsClick}
                                >
                                    {t('Extract all parts in this drawing to their own quote items')}
                                </MenuItem>
                            </Menu>

                            <Menu
                                anchorEl={anchorEl}
                                id="help-action-menu"
                                open={actionMenuOpen ? actionMenu === 'help' : null}
                                onClose={handleActionMenuClose}
                            >
                                <MenuItem onClick={toggleShowShortcuts}>
                                    <ListItemText sx={{ marginRight: 4 }}>
                                        {showShortcuts ? t('Hide shortcuts') : t('Show shortcuts')}
                                    </ListItemText>
                                    <Typography variant="body2">{`${isMacOS ? '\u2325' : 'Alt +'} S`}</Typography>
                                </MenuItem>
                                <MenuItem onClick={toggleShowLegend}>
                                    <ListItemText>
                                        {' '}
                                        {showLegend ? t('Hide path legend') : t('Show path legend')}
                                    </ListItemText>
                                    <Typography variant="body2">{`${isMacOS ? '\u2325' : 'Alt +'} P`}</Typography>
                                </MenuItem>
                            </Menu>
                        </Box>

                        <Box sx={[classes.topBarInfo(isTubePart), classes.flex]}>
                            <RenderDimensions
                                orderItemId={orderItemId}
                                selectedPaths={selectedPaths}
                            />
                            <IconButton
                                aria-label={t('Close drawing doctor')}
                                color="secondary"
                                size="small"
                                title={t('Close drawing doctor')}
                                onClick={onClose}
                            >
                                <HugeiconsIcon
                                    icon={Cancel01Icon}
                                    size={24}
                                />
                            </IconButton>
                        </Box>
                    </Box>
                    {isLoadingDrawing ? (
                        <LoadingOverlay labelText="Loading drawing..." />
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'row',
                                height: 'calc(100vh - 72px)',
                            }}
                        >
                            <DrawingDoctorDrawer
                                drawing={drawing}
                                orderItemId={orderItemId}
                                viewOnlyMode={viewOnlyMode}
                            />
                            <DrawingDoctor
                                drawing={drawing}
                                orderItemId={orderItemId}
                                originalCadSvg={cadView}
                                selectedPaths={selectedPaths}
                                showCadView={showCadView}
                                showLegend={showLegend}
                                showShortcuts={showShortcuts}
                                onSelectPath={setSelectedPaths}
                            />
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
            <ExtractPartDialog
                initialName={drawing?.name}
                multiple={false}
                open={showExtractPartDialog}
                onClose={hideShowExtractPartDialog}
                onSubmit={handleSelectedPathExtractionSubmit}
            />
            <ExtractPartDialog
                initialName={drawing?.name}
                multiple={true}
                open={showExtractAllPartsDialog}
                onClose={hideShowExtractAllPartDialog}
                onSubmit={handleAllPathExtractionSubmit}
            />
            <ScaleDrawingModal
                drawingDimensions={drawingDimensions}
                handleClose={() => {
                    setShowScaleDrawingModal(false)
                    setSelectedPaths({})
                }}
                open={showScaleDrawingModal}
            />
            <AlertDialog
                buttonVariant="contained"
                cancelButtonText={t('No, keep changes')}
                content={t(
                    'Are you sure you want to reset the drawing to its original state? This action cannot be undone.'
                )}
                okButtonText={t('Yes, reset drawing')}
                open={resetDrawingDialog}
                title={t('Confirm reset drawing')}
                onCancelClose={() => setResetDrawingDialog(false)}
                onOkClose={() => {
                    handleResetDrawing()
                    setResetDrawingDialog(false)
                }}
            />
        </ThemeProvider>
    )
}

DrawingDoctorModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
}

export default DrawingDoctorModal
